import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { validateEmail, validateForm } from '../../utils/validation';
import { forgotPasswordApi } from '../../services/authService';

function ForgotPassword() {
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validationRules = { email: validateEmail };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);

      try {
        const payload = {
          email: formData.email,
          domain: window.location.origin
        };

        await new Promise(resolve => setTimeout(resolve, 2000));
        const response = await forgotPasswordApi(payload);
        const otp = response?.data?.otp;

        toast.success('OTP sent successfully!', {
          position: "top-center",
          autoClose: 2000,
          onClose: () => {
            navigate('/auth/forgot-password-otp', {
              state: { email: formData.email, otp: otp }
            });
          }
        });
      } catch (error) {
        console.error('Failed to send OTP:', error);
        toast.error(error.response?.data?.message || 'Failed to send reset link', {
          position: "top-center",
          autoClose: 2000
        });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="min-h-screen bg-light-background flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Logo */}
      <div className="mb-6">
        <img src={getLogoByDomainAndAlt().logo} alt={getLogoByDomainAndAlt().alt} className="h-10 w-auto" />
      </div>
      
      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-font-primary mb-2">Forgot Password</h2>
          <p className="text-font-secondary text-sm">Enter your email to reset password</p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-font-primary mb-1.5">Email Address</label>
            <div className="relative">
              <Icon icon="mdi:email-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email" 
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.email ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
            </div>
            {errors.email && <span className="text-error text-xs mt-1 block">{errors.email[0]}</span>}
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Sending Reset Link...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
          
          {/* Login Link */}
          <p className="text-center text-sm text-font-secondary">
            Remember your password?{' '}
            <Link 
              to="/auth/login" 
              className={`text-primary hover:text-primary/80 font-medium ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
            >
              Login here
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ForgotPassword;
